import { useState, useEffect, useRef } from "react";
import { ArrowRight, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../../../utils/Images";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import authService from "../../../api/AuthService";
import { useDispatch, useSelector } from "react-redux";
import { setUser, clearAuth } from "../../../store/slices/authSlice";
import { completeSplash } from "../../../store/slices/onboardingSlice";

export default function VerifyAccount() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [code, setCode] = useState(["", "", "", ""]);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state (for signup flow) or Redux store (for redirects)
  const { 
    consumer_slug: stateSlug, 
    phone_number: statePhone, 
    email: stateEmail,
    from: redirectPath 
  } = location.state || {};
  
  // Add fallback logic and debugging
  const consumer_slug = stateSlug || user?.slug || user?.id;
  const phone_number = statePhone || user?.phone_number || user?.phone;
  const email = stateEmail || user?.email;
  


  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleInputChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input
      if (value && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.split("").concat(["", "", "", ""]).slice(0, 4);
      setCode(newCode);

      // Focus the next empty input or the last one
      const nextEmptyIndex = newCode.findIndex((digit, i) => !digit && i < 4);
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[3]?.focus();
      }
    }
  };

  const handleVerifyAccount = async () => {
    setIsSubmitting(true);
    try {
      const fullCode = code.join("");
      if (fullCode.length === 4) {
        const response = await authService.verifyAccount({
          slug: consumer_slug,
          otp: fullCode,
        });
        
        console.log("Verify Account Response:", response);
        
        if (response.status) {
          toast.success(response.message || "Account verified successfully!");
          // Update user data if available in response
          if (response.data?.data?.data) {
            dispatch(setUser(response.data.data.data));
          }
          dispatch(completeSplash());
          
          // Redirect to the original destination if available, otherwise go to home
          const destination = redirectPath || "/";
          navigate(destination, { replace: true });
        } else {
          toast.error(response.message || "Account verification failed!");
        }
      } else {
        toast.error("Please enter a valid code");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Verify Account Error:", error);
      toast.error(error.response?.data?.data?.reason || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (canResend) {
      setIsSubmitting(true);
      try {
        const response = await authService.resendVerificationCode({
          channel: phone_number ? "phone" : "email",
          email_or_phone: phone_number || email,
        });
        
        console.log("Resend Code Response:", response);
        
        if (response.status) {
          toast.success(response.message || "Code resent successfully!");
        } else {
          toast.error(response.message || "Failed to resend code");
        }
      } catch (error) {
        console.error("Resend Code Error:", error);
        toast.error("Failed to resend code");
      } finally {
        setCountdown(30);
        setCanResend(false);
        setCode(["", "", "", ""]);
        inputRefs.current[0]?.focus();
        setIsSubmitting(false);
      }
    }
  };

  const handleLogout = () => {
    dispatch(clearAuth());
    navigate("/signin");
    toast.info("Logged out successfully");
  };

  const isCodeComplete = code.every((digit) => digit !== "");

  // Show error state if no user data is available
  if (!user) {
    return (
      <div className="relative min-h-screen">
        <img
          src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1600&auto=format&fit=crop"
          alt="bg"
          className="object-cover absolute inset-0 w-full h-full"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="flex relative z-10 justify-center items-center p-6">
          <div className="p-6 mt-4 w-full max-w-md rounded-3xl shadow-2xl bg-white/95">
            <div className="text-center">
              <h2 className="mb-2 text-lg font-semibold text-gray-900">No User Data Found</h2>
              <p className="mb-4 text-gray-600">Unable to load user information for verification.</p>
              <button
                onClick={() => navigate("/signin")}
                className="px-4 py-2 text-white bg-emerald-600 rounded-lg transition-colors hover:bg-emerald-700"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <img
        src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1600&auto=format&fit=crop"
        alt="bg"
        className="object-cover absolute inset-0 w-full h-full"
      />
      <div className="absolute inset-0 bg-black/60" />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="flex relative z-10 justify-between items-start px-6 pt-10"
      >
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />

        {/* Logout Button */}
        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
          onClick={handleLogout}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white rounded-xl border backdrop-blur-sm transition-all duration-300 bg-red-500/80 border-red-400/30 hover:bg-red-500 hover:border-red-400 hover:shadow-lg"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="transition-transform duration-200 group-hover:-translate-x-0.5"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16,17 21,12 16,7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Logout
        </motion.button>
      </motion.div>

      <div className="flex relative z-10 justify-center items-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="p-6 mt-4 w-full max-w-md rounded-3xl shadow-2xl bg-white/95"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <h2 className="text-lg font-semibold text-center">
              Verify Account
            </h2>
            <p className="mt-1 text-xs text-center text-gray-500">
              Enter the 4-digit code sent to your {phone_number || email || "contact"}
            </p>
            <p className="mt-1 text-xs font-medium text-center text-emerald-600">
              {phone_number || email || "No contact information available"}
            </p>
          </motion.div>

          <div className="mt-6 space-y-6">
            {/* Code Input Circles */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
              className="flex gap-3 justify-center"
            >
              {code.map((digit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1, duration: 0.4 }}
                  className="relative group"
                >
                  <div className="relative rounded-full p-[2px] bg-gradient-to-br from-emerald-400/40 via-transparent to-emerald-400/40 transition-all duration-300 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                    <motion.input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      whileFocus={{ scale: 1.05 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 25,
                      }}
                      className="w-14 h-14 text-lg font-bold text-center rounded-full border-0 transition-all duration-300 outline-none bg-white/95 focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none focus:bg-white focus:text-emerald-700"
                      maxLength="1"
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Resend Code */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.3 }}
              className="text-center"
            >
              <AnimatePresence mode="wait">
                {canResend ? (
                  <motion.button
                    key="resend"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleResendCode}
                    className="inline-flex gap-2 items-center text-xs font-medium text-emerald-600 transition-colors hover:text-emerald-700"
                  >
                    <motion.div
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    >
                      <RotateCcw size={14} />
                    </motion.div>
                    Resend Code
                  </motion.button>
                ) : (
                  <motion.p
                    key="countdown"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="text-xs text-gray-500"
                  >
                    Resend code in
                    <motion.span
                      key={countdown}
                      initial={{ scale: 1.2, color: "#10b981" }}
                      animate={{ scale: 1, color: "#6b7280" }}
                      transition={{ duration: 0.2 }}
                      className="font-medium"
                    >
                      {countdown}
                    </motion.span>
                    s
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Verify Button */}
            <motion.button
              onClick={handleVerifyAccount}
              disabled={!isCodeComplete}
              whileHover={{ scale: isCodeComplete ? 1.02 : 1 }}
              whileTap={{ scale: isCodeComplete ? 0.98 : 1 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.4 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg transition-all duration-300 group hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex relative z-10 gap-2 justify-center items-center">
                {isSubmitting ? "Verifying..." : "Verify Code"}
                <motion.div
                  animate={isCodeComplete ? { x: [0, 3, 0] } : { x: 0 }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <ArrowRight size={16} />
                </motion.div>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </motion.button>

            {/* Back Link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.3 }}
            >
              <Link
                to="/forgot-password"
                className="block text-xs text-center text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Back to Enter Contact
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
